import { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { INFRA_TYPES, STATUS_COLORS, STATUS_LABELS, INFRA_STATUSES, formatMoney } from "@/lib/constants";
import { Plus, Edit, Trash2, Droplets } from "lucide-react";
import { useQuickAdd } from "@/lib/useQuickAdd";

function emptyInfra() {
  return {
    village_id: "", name: "", type: "icme_suyu", status: "planlama",
    start_date: "", end_date: "", budget: 0, actual_cost: 0, description: "",
  };
}

export default function InfrastructurePage() {
  const [items, setItems] = useState([]);
  const [villages, setVillages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInfra());

  const villageMap = useMemo(() => Object.fromEntries(villages.map((v) => [v.id, v.name])), [villages]);
  const grouped = useMemo(() => {
    const g = {};
    INFRA_STATUSES.forEach((s) => (g[s] = []));
    items.forEach((p) => { if (g[p.status]) g[p.status].push(p); });
    return g;
  }, [items]);

  const load = () => api.get("/infrastructure").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/villages").then((r) => setVillages(r.data));
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyInfra()); setOpen(true); };
  const quickDone = useQuickAdd((vid) => { setEditing(null); setForm({ ...emptyInfra(), village_id: vid }); setOpen(true); }, open);
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async () => {
    if (!form.village_id || !form.name) return toast.error("Köy ve proje adı zorunlu");
    try {
      if (editing) await api.put(`/infrastructure/${editing.id}`, form);
      else await api.post("/infrastructure", form);
      toast.success(editing ? "Güncellendi" : "Oluşturuldu");
      setOpen(false); load();
      if (!editing) quickDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (p) => {
    if (!window.confirm("Proje silinsin mi?")) return;
    await api.delete(`/infrastructure/${p.id}`);
    toast.success("Silindi"); load();
  };

  return (
    <AppLayout
      title="Altyapı Takibi"
      subtitle="İçme suyu, kanalizasyon ve sulama projeleri durum takibi"
      actions={
        <Button onClick={openNew} data-testid="new-infra-button" className="bg-emerald-700 hover:bg-emerald-800">
          <Plus className="h-4 w-4 mr-1.5" /> Yeni Proje
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="infra-kanban">
        {INFRA_STATUSES.map((s) => (
          <div key={s} className="flex flex-col" data-testid={`infra-column-${s}`}>
            <div className="flex items-center justify-between px-3 py-2 mb-3">
              <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[s]}`}>
                {STATUS_LABELS[s]}
              </div>
              <span className="text-xs text-slate-500 font-mono">{grouped[s].length}</span>
            </div>
            <div className="space-y-3 min-h-[200px]">
              {grouped[s].map((p) => (
                <Card key={p.id} className="p-4 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`infra-card-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <Droplets className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 text-sm truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{villageMap[p.village_id] || "-"}</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <Badge variant="outline" className="text-[10px]">{INFRA_TYPES[p.type]}</Badge>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="text-xs">
                      <span className="text-slate-500">Bütçe: </span>
                      <span className="font-mono font-semibold text-slate-900">{formatMoney(p.budget)}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(p)}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
              {grouped[s].length === 0 && (
                <div className="text-center text-xs text-slate-400 py-6 border border-dashed border-slate-200 rounded-md">
                  Kayıt yok
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="infra-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Projeyi Düzenle" : "Yeni Altyapı Projesi"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Proje Adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="infra-name-input" />
            </div>
            <div>
              <Label>Köy *</Label>
              <Select value={form.village_id} onValueChange={(v) => setForm({ ...form, village_id: v })}>
                <SelectTrigger data-testid="infra-village-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {villages.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tip</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(INFRA_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="infra-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INFRA_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Başlangıç</Label>
              <DateField value={form.start_date} className="w-full" testId="infra-start-date-input"
                     onChange={(v) => setForm({ ...form, start_date: v })} />
            </div>
            <div>
              <Label>Bitiş</Label>
              <DateField value={form.end_date} className="w-full" testId="infra-end-date-input"
                     onChange={(v) => setForm({ ...form, end_date: v })} />
            </div>
            <div>
              <Label>Bütçe (TL)</Label>
              <Input type="number" step="0.01" value={form.budget}
                     onChange={(e) => setForm({ ...form, budget: Number(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Gerçekleşen Maliyet</Label>
              <Input type="number" step="0.01" value={form.actual_cost}
                     onChange={(e) => setForm({ ...form, actual_cost: Number(e.target.value) || 0 })} />
            </div>
            <div className="col-span-2">
              <Label>Açıklama</Label>
              <Textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="infra-submit-button">
              {editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
